import { useState, useEffect, useRef } from 'react'
import { Login as SharedLogin, ConfirmationDialog, UserRole } from '@valiant/ui-shared'
import { useAuth } from '../AuthProvider'
import { Auth } from '../auth'
import { useNavigate } from 'react-router-dom'
import { useGetMaintenanceModeQuery } from '../generated/graphql'
import { SuperAdminImpersonateStep } from '../components/SuperAdminImpersonateStep'

export function Login() {
  const authProvider = useAuth()
  const [authInstance] = useState(() => new Auth())
  const navigate = useNavigate()
  const [loggedIn, setLoggedIn] = useState(false)
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false)
  const handled = useRef(false)
  const [adminToken, setAdminToken] = useState<string | null>(null)

  const [{ data: maintenanceData, fetching }] = useGetMaintenanceModeQuery({
    pause: !loggedIn,
  })

  useEffect(() => {
    if (!loggedIn || fetching || handled.current) return
    handled.current = true

    if (maintenanceData?.getMaintenanceMode) {
      setShowMaintenanceDialog(true)
    } else {
      navigate('/', { replace: true })
    }
  }, [loggedIn, fetching, maintenanceData, navigate])

  const handleLoginSuccess = () => {
    if (authProvider.auth?.role === UserRole.GroupOwner) {
      localStorage.setItem('dashboardMode', 'admin')
    }
    setLoggedIn(true)
  }

  const handleAdminLoginSuccess = (token: string) => {
    setAdminToken(token)
  }

  const handleImpersonateSuccess = async (email: string) => {
    if (!adminToken) return
    const { token, refreshToken } = await authInstance.impersonate(adminToken, email)
    const { role, userId } = authProvider.decodeToken(token)
    authProvider.setAuth({ token, refreshToken, role, userId })
    handleLoginSuccess()
  }

  const handleMaintenanceDismiss = () => {
    setShowMaintenanceDialog(false)
    navigate('/', { replace: true })
  }

  if (adminToken) {
    return (
      <SuperAdminImpersonateStep
        onImpersonate={handleImpersonateSuccess}
        onCancel={() => setAdminToken(null)}
      />
    )
  }

  return (
    <>
      <SharedLogin
        authInstance={authInstance}
        authProvider={authProvider}
        variant="user"
        onLoginSuccess={handleLoginSuccess}
        onAdminLoginSuccess={handleAdminLoginSuccess}
      />

      <ConfirmationDialog
        open={showMaintenanceDialog}
        onOpenChange={(open) => {
          if (!open) handleMaintenanceDismiss()
        }}
        title="Platform Maintenance"
        description="The platform is currently in maintenance mode. Please do not deposit funds at this time, as swaps are temporarily unavailable."
        confirmText="OK"
        hideCancelButton={true}
        onConfirm={handleMaintenanceDismiss}
        variant="default"
      />
    </>
  )
}
